import { fail, handleError, isFail, Result, success } from '../common/serviceUtil';
import { getHeaders, predictorProxy } from './proxies';
import {
  Entry,
  GetEntriesResponse,
  GetPredictorsResponse,
  GetPredictorStatsResponse,
  Pick,
  Predictor,
} from '../interfaces/predictors';
import { SportCode } from '@incentivegames/ig-types/lib/enums/sport/sportCode';
import { devTools } from '../common/devtools';
import { minsDivisor } from '../common/dateUtils';

export const PredictorEndpoints = {
  GetPredictors: '/predictors',
  GetEntries: '/entries',
  GetHistoricEntries: `/entries`,
} as const;
type PredictorEndpointKey = keyof typeof PredictorEndpoints;
export type PredictorEndpoint = typeof PredictorEndpoints[PredictorEndpointKey];

const getPredictorsInternal = async (
  signal: AbortSignal | undefined,
  pageNo: number,
  countryCode?: string,
): Promise<Result<GetPredictorsResponse, unknown>> => {
  devTools.log('Getting Predictors page', pageNo);
  try {
    const response = await predictorProxy.get<GetPredictorsResponse>(PredictorEndpoints.GetPredictors, {
      signal,
      params: {
        predictorType: 'pick6',
        sportCode: SportCode.horseRacing,
        pageSize: process.env.PREDICTOR_PAGE_SIZE ?? 50,
        pageNumber: pageNo,
        countryCode,
      },
      headers: getHeaders(),
    });
    return success(response.data);
  } catch (error) {
    return fail(handleError(error));
  }
};
export const getPredictors = async (
  signal: AbortSignal | undefined,
  countryCode?: string,
): Promise<Result<Predictor[], unknown>> => {
  let result: Result<GetPredictorsResponse, unknown>;
  const predictors: Predictor[] = [];
  let pageNo = 0;
  do {
    result = await getPredictorsInternal(signal, ++pageNo, countryCode);
    if (isFail(result)) {
      return result;
    }
    predictors.push(...result.value.predictions);
  } while (result.value.paging.hasNextPage);
  return success(predictors);
};

interface GetEntryProps {
  userToken?: string;
  predictorId?: number;
  signal: AbortSignal | undefined;
}

export const getEntry = async ({ userToken, predictorId, signal }: GetEntryProps): Promise<Result<Entry, unknown>> => {
  if (!predictorId) return fail(handleError(new Error('Predictor Id invalid')));
  if (!userToken) return fail(handleError(new Error('userToken invalid')));

  try {
    const response = await predictorProxy.get<GetEntriesResponse>(PredictorEndpoints.GetEntries, {
      signal,
      params: {
        predictorId,
        predictorType: 'pick6',
        sportCode: SportCode.horseRacing,
      },
      headers: getHeaders(userToken),
    });
    return success(response.data.entries[0]);
  } catch (error) {
    return fail(handleError(error));
  }
};

interface UpsertEntry {
  token?: string;
  payload: {
    uniqueId: string;
    predictorId?: number;
    picks: Pick[];
    countryCode?: string;
    countryRegion?: string;
  };
  receipt?: string;
}

export const postEntry = async ({ token, payload }: UpsertEntry) => {
  if (!token) return fail('token invalid');
  if (!payload.predictorId) return fail('predictorId invalid');

  try {
    const response = await predictorProxy.post<Entry>(`/entry`, payload, { headers: getHeaders(token) });
    if (response.data.hasOwnProperty('errorCode')) {
      return fail(handleError(response.data));
    }
    return success(response.data);
  } catch (error) {
    return fail(handleError(error));
  }
};

export const putEntry = async ({ token, payload, receipt }: UpsertEntry) => {
  if (!receipt) return fail('receipt invalid');
  if (!token) return fail('token invalid');
  if (!payload.predictorId) return fail('predictorId invalid');

  try {
    const response = await predictorProxy.put<Entry>(`/entry/${receipt}`, payload, { headers: getHeaders(token) });
    // @ts-ignore we could receive an error code here
    if (response.data.hasOwnProperty('errorCode')) {
      return fail(handleError(response.data));
    }
    return success(response.data);
  } catch (error) {
    return fail(handleError(error));
  }
};

interface GetPredictorStatsProps {
  predictorId?: number;
  signal: AbortSignal | undefined;
}

export const getPredictorStats = async ({ predictorId, signal }: GetPredictorStatsProps) => {
  if (!predictorId) return fail(handleError(new Error('Predictor Id invalid')));
  try {
    const response = await predictorProxy.get<GetPredictorStatsResponse>(`/predictorStats`, {
      signal,
      params: {
        predictorId,
      },
      headers: getHeaders(),
    });
    return success(response.data);
  } catch (error) {
    return fail(handleError(error));
  }
};

export const getHistoricEntries = async ({
  userToken,
  signal,
}: {
  userToken?: string;
  signal: AbortSignal | undefined;
}): Promise<GetEntriesResponse> => {
  if (!userToken) throw new Error('userToken invalid');
  const response = await predictorProxy.get<GetEntriesResponse>(PredictorEndpoints.GetHistoricEntries, {
    params: {
      predictorType: 'pick6',
      status: 'settled',
      sportCode: SportCode.horseRacing,
      toTime: new Date().getTime() - minsDivisor,
    },
    headers: getHeaders(userToken),
    signal,
  });
  return response.data;
};
